import {
  Button,
  Icon,
  IconButton,
  Toggle,
  useResponsiveValue,
} from '@monash/portal-react';
import { useMemo, useRef, useState } from 'react';
import mapAdvancedStandings from '../../../utilities/data-transform/map-advanced-standings';
import c from './units-and-results.module.scss';
import Year from './year/Year';

export const MOBILE_RESPONSIVE = [
  {
    mq: '(max-width: 799px)',
    value: 'S',
  },
  {
    mq: '(min-width: 800px)',
    value: 'L',
  },
];

const UnitsAndResults = ({
  years,
  priorStudy,
  openGuideModal,
  defaultViewOptionIndex,
  earliestFirst,
}) => {
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const viewOptions = [
    {
      value: 'List View',
      icon: <Icon.LayoutList />,
      ariaLabel: 'View units in list layout',
    },
    {
      value: 'Grid View',
      icon: <Icon.LayoutGrid />,
      ariaLabel: 'View units in grid layout',
    },
  ];

  const [view, setView] = useState(viewOptions[defaultViewOptionIndex]);
  const [earliestToLatest, setEarliestToLatest] = useState(earliestFirst);

  // Unit and results
  const record = useMemo(
    () =>
      priorStudy.length
        ? [
            // Add formatted advanced standings to start of unit results
            mapAdvancedStandings(priorStudy),
            ...Object.values(years),
          ]
        : Object.values(years),
    [years, priorStudy]
  );

  // Sorting
  const sortedRecord = earliestToLatest ? record : [...record].reverse();
  const guideRef = useRef();

  return (
    <section className={c.academicRecord} aria-label="Unit and results">
      <div className={c.headerGroup}>
        <div className={c.header}>
          <div className={c.title}>
            <h2>Units and results</h2>{' '}
            <IconButton
              ref={guideRef}
              onClick={() => openGuideModal(guideRef)}
              icon={Icon.InfoCircle}
              aria-label="Glossary"
              aria-haspopup="dialog"
            />
          </div>

          <div className={c.print}>
            <IconButton
              onClick={print}
              icon={Icon.Printer}
              aria-label="Print"
            />
          </div>
        </div>
        <div
          className={c.actionGroup}
          aria-label="Unit and results toolbar"
          role="region"
        >
          <div className={c.action}>
            <Button
              onClick={() => {
                setEarliestToLatest((f) => !f);
              }}
              variant="text"
              iconPosition="right"
              size="small"
              icon={
                earliestToLatest ? (
                  <Icon.ArrowDown size={24} />
                ) : (
                  <Icon.ArrowUp size={24} />
                )
              }
            >
              {earliestToLatest ? 'Earliest first' : 'Latest first'}
            </Button>
          </div>
          <div className={c.action}>
            <Toggle
              ariaLabel="Change view type"
              options={viewOptions}
              selected={view}
              setSelected={setView}
              iconsOnly={responsiveSize === 'S'}
            />
          </div>
        </div>
      </div>

      <div className={c.years}>
        {sortedRecord.map((year) => (
          <Year
            year={year}
            listView={view.value === 'List View'}
            earliestToLatest={earliestToLatest}
            key={year.id}
            openGuide={openGuideModal}
          />
        ))}
      </div>
    </section>
  );
};

export default UnitsAndResults;
